//Trading-dashboad.tsx
'use client';

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import LoadingLogo from '@/components/component/loading-screen/animated-logo';
import { useAuth } from '@/lib/AuthContext';
import { ResolutionString } from '@/public/charting_library-master/charting_library/charting_library';
import { IChartingLibraryWidget } from '@/public/charting_library-master/charting_library/charting_library';
import {
  loadWatchData,
  renderAuthScreen,
  globalDatafeedInstance,
} from './trading-dashboard-utils';

declare global {
  interface Window {
    TradingView: any;
    tvReady?: boolean;
    tvWidget?: any;
    forceChartSymbolUpdate?: (symbol: string) => boolean;
  }
}

interface TradingDashboardProps {
  onLoadComplete?: () => void;
  isTvLibraryReady?: boolean;
}

// Add this new component before the TradingDashboard component
const AnimatedEllipsis = () => {
  return (
    <span className="inline-flex">
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.3,
          repeat: Infinity,
          repeatType: 'reverse',
          repeatDelay: 0.1,
        }}
      >
        .
      </motion.span>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.3,
          repeat: Infinity,
          repeatType: 'reverse',
          repeatDelay: 0.2,
          delay: 0.1,
        }}
      >
        .
      </motion.span>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.3,
          repeat: Infinity,
          repeatType: 'reverse',
          repeatDelay: 0.3,
          delay: 0.2,
        }}
      >
        .
      </motion.span>
    </span>
  );
};

export function TradingDashboard({
  onLoadComplete,
  isTvLibraryReady = false,
}: TradingDashboardProps) {
  const { user } = useAuth();
  const [isGuest, setIsGuest] = useState(false);
  const [tvWidget, setTvWidget] = useState<IChartingLibraryWidget | null>(null);
  const [watchData, setWatchData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [chartInitialized, setChartInitialized] = useState(false);
  const [initializationAttempted, setInitializationAttempted] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Load watch data once on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        console.log('Loading initial watch data...');
        const data = await loadWatchData();
        if (data && data.length > 0) {
          setWatchData(data);
          console.log(`Loaded ${data.length} watch data items`);
        } else {
          console.warn('No watch data returned from API');
        }
      } catch (error) {
        console.error('Error loading initial watch data:', error);
      }
    };

    loadInitialData();
  }, []);

  const initializeWidget = async () => {
    // Prevent multiple initialization attempts
    if (initializationAttempted) {
      console.log('Chart initialization already attempted, skipping');
      return;
    }

    setInitializationAttempted(true);

    if (!chartContainerRef.current || (!user && !isGuest) || chartInitialized) {
      console.log('Skipping chart initialization - prerequisites not met', {
        containerExists: !!chartContainerRef.current,
        userOrGuest: !!(user || isGuest),
        chartInitialized,
      });
      setIsLoading(false);
      onLoadComplete?.();
      return;
    }

    try {
      console.log('Starting chart initialization');

      // Check if TradingView library is loaded
      if (!window.TradingView) {
        console.log('TradingView library not available yet, waiting...');

        // Wait for tvReady flag from parent
        if (!window.tvReady && !isTvLibraryReady) {
          // Create a promise that resolves when the library is loaded
          await new Promise<void>((resolve) => {
            // Set up event listener for the tvLibraryLoaded event
            const handleTvLoaded = () => {
              console.log('TradingView library loaded event detected');
              resolve();
            };

            window.addEventListener('tvLibraryLoaded', handleTvLoaded, {
              once: true,
            });

            // Failsafe timeout after 5 seconds
            setTimeout(() => {
              window.removeEventListener('tvLibraryLoaded', handleTvLoaded);
              console.warn('Timed out waiting for TradingView library');
              resolve();
            }, 5000);
          });
        }

        // Final check after waiting
        if (!window.TradingView) {
          console.error(
            'TradingView library still not available after waiting'
          );
          throw new Error('TradingView library not loaded');
        }
      }

      // Delay to ensure container is fully rendered
      await new Promise((resolve) => setTimeout(resolve, 300));

      const container = chartContainerRef.current;
      if (!container) {
        console.error('Chart container is null');
        throw new Error('Container is null');
      }

      // Make sure the container has dimensions - Removed forcing dimensions, relying on CSS now
      if (container.clientWidth === 0 || container.clientHeight === 0) {
        console.warn(
          'Container has zero dimensions initially, relying on CSS to size it.'
        );
      }

      console.log('Container dimensions:', {
        width: container.clientWidth,
        height: container.clientHeight,
      });

      // Force prefetch data range to ensure it's available
      try {
        console.log('Pre-fetching data range for default symbols');
        await Promise.all([
          fetch(
            '/api/trading-dashboard/getDataRange?symbol=Rolex:16610&source=forum'
          ),
          // fetch(
          //   '/api/trading-dashboard/getDataRange?symbol=METAL:GOLD&source=forum'
          // ),
          // fetch(
          //   '/api/trading-dashboard/getDataRange?symbol=INDEX:SP500&source=forum'
          // ),
          // fetch(
          //   '/api/trading-dashboard/getDataRange?symbol=INFLATION:RATE&source=forum'
          // ),
          // fetch(
          //   '/api/trading-dashboard/getDataRange?symbol=Rolex:16710&source=forum'
          // ),
          // fetch(
          //   '/api/trading-dashboard/getDataRange?symbol=Rolex:116520&source=forum'
          // ),
        ]);
        console.log('Successfully pre-fetched data ranges');
      } catch (prefetchError) {
        console.warn('Error pre-fetching data ranges:', prefetchError);
      }

      // Prepare symbol data before creating widget
      console.log('Pre-initializing data for Rolex:16610');
      try {
        await globalDatafeedInstance.initializeDataForSymbol('Rolex:16610');
      } catch (preloadError) {
        console.warn('Pre-initialization of data failed:', preloadError);
      }

      const isMobileView = window.innerWidth < 768;
      console.log('Creating TradingView widget');

      const widget = new window.TradingView.widget({
        debug: false, // Keep debug off for production, set to true for more logging
        symbol: 'Rolex:16610',
        interval: '1W' as ResolutionString,
        container: container,
        datafeed: globalDatafeedInstance,
        library_path: '/charting_library-master/charting_library/',
        locale: 'en',
        disabled_features: [
          'use_localstorage_for_settings',
          'header_screenshot',
          'timeframes_toolbar',
          'header_watchlist', // Disable the native watchlist
          'watchlist_context_menu', // Disable context menu for watchlist items
          'create_volume_indicator_by_default', // Optional: removes the volume panel
          ...(isMobileView ? ['left_toolbar'] : []),
        ],
        enabled_features: [
          'show_exchange_logos',
          'header_symbol_search',
          'symbol_search_hot_key',
          'header_chart_type',
          'header_settings',
          'header_indicators',
          'header_compare',
          'header_fullscreen_button',
          'high_density_bars', // Improves data visualization quality
          'smooth_lines', // Makes the chart lines smoother
          'side_toolbar_in_fullscreen_mode',
        ],
        theme: 'dark',
        fullscreen: false,
        autosize: true, // Keep autosize true for responsiveness
        overrides: {
          'mainSeriesProperties.style': 2, // Use 1 for line chart (smoother)
          'mainSeriesProperties.showPriceLine': true,
          'mainSeriesProperties.priceAxisProperties.autoScale': true,
          'mainSeriesProperties.priceAxisProperties.logarithmic': false,
          'mainSeriesProperties.priceAxisProperties.alignLabels': true,
          'mainSeriesProperties.visible': true,
          'mainSeriesProperties.showCountdown': true,
          'mainSeriesProperties.minTick': '1',
          'mainSeriesProperties.statusViewStyle.fontSize': 13,
          'mainSeriesProperties.precision': 2,
          'paneProperties.backgroundType': 'solid',
          'paneProperties.background': '#0f172a',
          'paneProperties.vertGridProperties.color': '#1e293b',
          'paneProperties.horzGridProperties.color': '#1e293b',
          'paneProperties.vertGridProperties.style': 0,
          'paneProperties.horzGridProperties.style': 0,
          'paneProperties.vertGridProperties.width': 1,
          'paneProperties.horzGridProperties.width': 1,
          'paneProperties.crossHairProperties.color': '#9ca3af',
          'paneProperties.crossHairProperties.width': 1,
          'paneProperties.crossHairProperties.style': 2,
          'paneProperties.topMargin': 5,
          'paneProperties.bottomMargin': 5,
          ...(isMobileView && {
            'paneProperties.legendProperties.showSeriesTitle': true,
            'paneProperties.legendProperties.showLegend': true,
            'paneProperties.legendProperties.showBarChange': true,
            'paneProperties.legendProperties.showBackground': true,
            'paneProperties.leftMargin': 8,
            'paneProperties.rightMargin': 8,
            'priceScale.alignLabels': true,
            'priceScale.borderVisible': false,
          }),
          'scalesProperties.textColor': '#94a3b8',
          'scalesProperties.lineColor': '#334155',
          'scalesProperties.fontSize': isMobileView ? 10 : 12,
          'timeScale.timeVisible': true,
          'timeScale.secondsVisible': false,
          'timeScale.rightOffset': 5,
          'timeScale.barSpacing': 6,
          'timeScale.minBarSpacing': 0.5,
          'timeScale.fixLeftEdge': true,
          'timeScale.lockVisibleTimeRangeOnResize': true,
          'timeScale.rightBarStaysOnScroll': true,
          'timeScale.borderVisible': true,
          'mainSeriesProperties.candleStyle.upColor': '#10b981',
          'mainSeriesProperties.candleStyle.downColor': '#ef4444',
          'mainSeriesProperties.candleStyle.drawWick': true,
          'mainSeriesProperties.candleStyle.drawBorder': true,
          'mainSeriesProperties.candleStyle.borderUpColor': '#10b981',
          'mainSeriesProperties.candleStyle.borderDownColor': '#ef4444',
          'mainSeriesProperties.candleStyle.wickUpColor': '#10b981',
          'mainSeriesProperties.candleStyle.wickDownColor': '#ef4444',
          'mainSeriesProperties.priceFormat.precision': 2,
          'mainSeriesProperties.priceFormat.minMove': 0.01,
          'mainSeriesProperties.lineStyle.color': '#3b82f6',
          'mainSeriesProperties.lineStyle.linestyle': 0,
          'mainSeriesProperties.lineStyle.linewidth': 2,
          'mainSeriesProperties.lineStyle.priceSource': 'close',
          'mainSeriesProperties.areaStyle.color1': 'rgba(59, 130, 246, 0.2)',
          'mainSeriesProperties.areaStyle.color2': 'rgba(59, 130, 246, 0.05)',
          'mainSeriesProperties.areaStyle.linecolor': '#3b82f6',
          'mainSeriesProperties.areaStyle.linestyle': 0,
          'mainSeriesProperties.areaStyle.linewidth': 2,
          'mainSeriesProperties.areaStyle.priceSource': 'close',
        },
        loading_screen: { backgroundColor: '#0f172a' },
        custom_css_url: '/charting_library-master/custom-styles.css',
        studies_overrides: {
          'volume.volume.color.0': 'rgba(239, 68, 68, 0.5)',
          'volume.volume.color.1': 'rgba(16, 185, 129, 0.5)',
          'volume.volume.transparency': 70,
          'moving average.style': 'squares',
          'moving average.color': '#3b82f6',
          'moving average exponential.style': 'squares',
          'moving average exponential.color': '#8b5cf6',
        },
      });

      // Make widget available globally - IMPORTANT for symbol switching
      window.tvWidget = widget;

      // Dispatch event that widget is ready
      try {
        const tvWidgetReadyEvent = new CustomEvent('tvWidgetReady', {
          detail: { widget: widget },
          bubbles: true,
        });
        window.dispatchEvent(tvWidgetReadyEvent);
        console.log('Dispatched tvWidgetReady event');
      } catch (e) {
        console.error('Error dispatching tvWidgetReady event:', e);
      }

      // Wait for chart to be ready with a timeout
      let chartReady = false;
      try {
        await Promise.race([
          new Promise<void>((resolve) => {
            widget.onChartReady(() => {
              console.log('Chart is ready');
              chartReady = true;

              // Apply additional customizations after chart is ready
              try {
                // Increase visibility of main line
                widget.applyOverrides({
                  'mainSeriesProperties.lineStyle.linewidth': 2.5,
                  'mainSeriesProperties.lineStyle.color': '#3b82f6',
                });
              } catch (err) {
                console.warn('Error applying post-ready overrides:', err);
              }

              // Dispatch chart ready event
              try {
                const tvChartReadyEvent = new CustomEvent('tvChartReady', {
                  detail: { widget: widget },
                  bubbles: true,
                });
                window.dispatchEvent(tvChartReadyEvent);
                console.log('Dispatched tvChartReady event');
              } catch (e) {
                console.error('Error dispatching tvChartReady event:', e);
              }

              resolve();
            });
          }),
          new Promise<void>((_, reject) => {
            setTimeout(() => reject(new Error('Chart ready timeout')), 10000);
          }),
        ]);
      } catch (timeoutError) {
        console.warn('Chart ready timeout:', timeoutError);
        // Forcibly trigger next steps even if chart didn't signal ready
      }

      setTvWidget(widget);
      setChartInitialized(true);
      setIsLoading(false);
      onLoadComplete?.();

      // If chart is not ready yet but the UI has moved on, force a reload of the symbol
      if (!chartReady) {
        setTimeout(() => {
          try {
            if (widget) {
              const currentSymbol =
                widget.symbolInterval()?.symbol || 'Rolex:16610';
              const currentInterval = widget.symbolInterval()?.interval || '1D';
              console.log('Forcing symbol refresh', {
                currentSymbol,
                currentInterval,
              });

              // Reset and then set the symbol to force reload
              widget.setSymbol('', currentInterval as ResolutionString, () => {
                setTimeout(() => {
                  widget.setSymbol(
                    currentSymbol,
                    currentInterval as ResolutionString
                  );
                }, 500);
              });
            }
          } catch (e) {
            console.error('Error in delayed symbol refresh:', e);
          }
        }, 5000);
      }

      console.log('Chart widget initialized successfully');
    } catch (error) {
      console.error('Chart initialization failed:', error);
      setIsLoading(false);
      onLoadComplete?.();
    }
  };

  // Initialize chart when conditions are met
  useEffect(() => {
    if (
      watchData.length &&
      (user || isGuest) &&
      !chartInitialized &&
      !initializationAttempted
    ) {
      console.log('Conditions met for widget initialization');
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initializeWidget();
      }, 300);
      return () => clearTimeout(timer);
    }

    return () => {
      if (tvWidget) {
        try {
          tvWidget.remove();
          setTvWidget(null);
          setChartInitialized(false);
        } catch (error) {
          console.error('Error cleaning up widget:', error);
        }
      }
    };
  }, [
    watchData,
    user,
    isGuest,
    chartInitialized,
    initializationAttempted,
    isTvLibraryReady,
  ]);

  // Preload common symbols
  useEffect(() => {
    if (globalDatafeedInstance) {
      console.log('Preloading common symbols data');
      // Preload verified symbols from the API, including economic indicators
      // Rolex:INDEX will use mock data if the real API fails
      [
        'Rolex:16610',
        // 'Rolex:16710',
        // 'Rolex:116520',
        // 'Rolex:INDEX',
        // 'METAL:GOLD',
        // 'INDEX:SP500',
        // 'INFLATION:RATE',
      ].forEach((symbol) => {
        setTimeout(() => {
          globalDatafeedInstance.resolveSymbol(
            symbol,
            (symbolInfo) => {
              console.log(`Symbol ${symbol} resolved successfully`);
              // After resolving, preload the historical data
              globalDatafeedInstance
                .initializeDataForSymbol(symbol)
                .then((bars) => {
                  if (bars && bars.length > 0) {
                    console.log(
                      `Pre-fetched ${bars.length} bars for ${symbol}`
                    );
                  }
                })
                .catch((err) =>
                  console.warn(`Error pre-fetching bars for ${symbol}:`, err)
                );
            },
            (error) => {
              console.warn(`Failed to resolve ${symbol}:`, error);
            }
          );
        }, 100); // Small delay to prevent overwhelming the browser
      });
    }
  }, []);

  // Failsafe for loading state
  useEffect(() => {
    const maxLoadingTime = 15000; // 15 seconds max loading time
    if (isLoading) {
      const timer = setTimeout(() => {
        console.log('Forcing loading screen to close (timeout)');
        setIsLoading(false);
        onLoadComplete?.();
      }, maxLoadingTime);
      return () => clearTimeout(timer);
    }
  }, [isLoading, onLoadComplete]);

  // Show auth screen if not logged in
  if (!user && !isGuest) {
    return renderAuthScreen();
  }

  return (
    <div className="flex flex-col md:flex-row h-screen overflow-hidden bg-slate-900 text-white">
      <div
        ref={chartContainerRef}
        className="flex-1 h-full bg-slate-900 relative"
      />

      {/* Component level loading animation with animated logo */}
      <AnimatePresence mode="wait">
        {isLoading && !chartInitialized && (
          <motion.div
            key="component-loading"
            className="fixed inset-0 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-800/90 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col items-center justify-center">
              <motion.div
                className="w-24 h-24 mb-4"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
              >
                <LoadingLogo />
              </motion.div>

              <motion.p
                className="text-white/70 text-sm"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.3 }}
              >
                Initializing Chart
                <AnimatedEllipsis />
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add this invisible div that is removed once loading completes */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        body.chart-loaded .chart-container {
          pointer-events: auto !important;
        }

        /* This ensures any leftover overlay divs don't block interaction */
        .tv-chart-container * {
          pointer-events: auto !important;
        }

        /* Fix for the specific div that might be causing the issue based on dev tools */
        div.fixed.inset-0:not([class*='loading']) {
          pointer-events: none !important;
        }
        `
        }}
      />
    </div>
  );
}
